import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookmarkPlus, Check, Loader2, Plus, Inbox, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SaveToLibraryButton — reusable "Save to My Library" trigger for any
 * generator page (LessonPlanBuilder, RubricGenerator, SkeletonNotes).
 *
 * Props:
 *   artifactType   'lesson_plan' | 'skeleton_notes' | 'rubric'
 *   title          string (default title, editable in the picker dialog)
 *   description?   string
 *   gradeLevel?    number
 *   unitId?        string
 *   unitTitle?     string
 *   content        object — the full artifact payload
 *   variant?       button variant (default outline)
 *   size?          button size
 */
export default function SaveToLibraryButton({
  artifactType,
  title,
  description = '',
  gradeLevel,
  unitId,
  unitTitle = '',
  content,
  variant = 'outline',
  size = 'sm',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('root');
  const [titleDraft, setTitleDraft] = useState(title || '');
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/library/folders`);
      setFolders(data.folders || []);
    } catch (err) {
      // Non-fatal — dialog still works with root folder available
      console.warn('SaveToLibraryButton: failed to load folders', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setTitleDraft(title || '');
      setSaved(false);
      setSelectedFolder('root');
      setNewFolderName('');
      setCreatingFolder(false);
      loadFolders();
    }
  }, [open, title, loadFolders]);

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/library/folders`, { name });
      toast.success(`Folder "${name}" created`);
      setFolders((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedFolder(data.id);
      setNewFolderName('');
      setCreatingFolder(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleSave = async () => {
    const finalTitle = titleDraft.trim();
    if (!finalTitle) {
      toast.error('Please provide a title');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/library/lessons`, {
        artifact_type: artifactType,
        title: finalTitle,
        description,
        grade_level: gradeLevel,
        unit_id: unitId,
        unit_title: unitTitle,
        folder_id: selectedFolder === 'root' ? null : selectedFolder,
        content: content || {},
      });
      setSaved(true);
      toast.success('Saved to your library');
      // Auto-close after brief confirmation
      setTimeout(() => setOpen(false), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const disabled = !content || (Array.isArray(content) && content.length === 0);

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={`border-[#D4AF37]/40 text-[#14213D] hover:bg-[#FBF6E8] ${className}`}
        data-testid="save-to-library-btn"
      >
        <BookmarkPlus className="h-4 w-4 mr-1.5" /> Save to Library
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-to-library-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl text-[#14213D]">Save to My Library</DialogTitle>
            <DialogDescription className="text-xs text-[#5A6577]">
              Pick a folder or leave in Uncategorized. You can move it later.
            </DialogDescription>
          </DialogHeader>

          {saved ? (
            <div className="py-8 text-center" data-testid="save-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-3">
                <Check className="h-5 w-5 text-emerald-600" strokeWidth={2} />
              </div>
              <p className="text-sm text-[#14213D] font-semibold">Saved</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="lib-title" className="text-xs uppercase tracking-wider text-[#5A6577]">Title</Label>
                <Input
                  id="lib-title"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  maxLength={200}
                  data-testid="save-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#5A6577]">Folder</Label>
                <div className="space-y-1 max-h-48 overflow-y-auto -mx-1 px-1 py-0.5 border border-[#EBE7E0] rounded-md">
                  <FolderRow
                    label="Uncategorized"
                    icon={Inbox}
                    color="#718096"
                    selected={selectedFolder === 'root'}
                    onSelect={() => setSelectedFolder('root')}
                    testId="save-folder-root"
                  />
                  {folders.map((f) => (
                    <FolderRow
                      key={f.id}
                      label={f.name}
                      icon={Folder}
                      color={f.color}
                      selected={selectedFolder === f.id}
                      onSelect={() => setSelectedFolder(f.id)}
                      testId={`save-folder-${f.id}`}
                    />
                  ))}
                </div>
              </div>

              {creatingFolder ? (
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="new-folder" className="text-[11px] uppercase tracking-wider text-[#5A6577]">New folder name</Label>
                    <Input
                      id="new-folder"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateFolder())}
                      placeholder="e.g., Grade 10 Civics"
                      maxLength={80}
                      autoFocus
                      data-testid="new-folder-input"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim()}
                    className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
                    data-testid="create-folder-confirm-btn"
                  >
                    Create
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setCreatingFolder(false)}>Cancel</Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreatingFolder(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-[#14213D] hover:underline"
                  data-testid="show-new-folder-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Create new folder
                </button>
              )}
            </div>
          )}

          {!saved && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || !titleDraft.trim()}
                className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
                data-testid="save-confirm-btn"
              >
                {saving ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>) : 'Save'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const FolderRow = ({ label, icon: Icon, color, selected, onSelect, testId }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm ${
      selected ? 'bg-[#F9F8F4] text-[#14213D]' : 'text-[#2D3748] hover:bg-[#F9F8F4]'
    }`}
    data-testid={testId}
  >
    <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color }} strokeWidth={1.75} />
    <span className="truncate font-medium">{label}</span>
    {selected && <Check className="h-3.5 w-3.5 ml-auto text-[#D4AF37]" strokeWidth={2} />}
  </button>
);
